import {
    autoUpdate,
    flip,
    arrow as floatingArrow,
    FloatingPortal,
    offset,
    Placement,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from "@floating-ui/react";
import {
    cloneElement,
    CSSProperties,
    MouseEvent,
    ReactElement,
    ReactNode,
    Ref,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import "./Tooltip.scss";

export const TOOLTIP_LOCK_DELAY_MS = 666;

interface IProps {
    hide?: boolean;
    title: ReactNode;
    children: ReactElement;
    placement?: Placement;
    offset?: number;
    show?: boolean;
    defaultShow?: boolean;
    trigger?: "hover" | "focus" | "click" | "manual" | Array<"hover" | "focus" | "click" | "manual">;
    interactive?: boolean;
    className?: string;
    arrowClassName?: string;
    style?: CSSProperties;
    onOpenChange?: (open: boolean) => void;
    onMouseEnter?: (e: MouseEvent<HTMLDivElement>) => void;
    onMouseLeave?: (e: MouseEvent<HTMLDivElement>) => void;
}

export default function Tooltip({
    title,
    children,
    onMouseEnter,
    onMouseLeave,
    hide,
    placement = "top",
    offset: offsetValue = 8,
    show,
    defaultShow = false,
    trigger = "hover",
    interactive = true,
    className,
    arrowClassName,
    style,
    onOpenChange,
}: IProps) {
    if (!title) {
        return children;
    }

    if (hide) {
        return children;
    }

    const isControlled = typeof show === "boolean";
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultShow);
    const open = isControlled ? show : uncontrolledOpen;
    const [locked, setLocked] = useState(false);

    const handleOpenChange = (nextOpen: boolean) => {
        if (!isControlled) {
            setUncontrolledOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
    };

    const triggers = useMemo(() => {
        const list = Array.isArray(trigger) ? trigger : [trigger];
        return new Set(list);
    }, [trigger]);

    const arrowRef = useRef<HTMLDivElement | null>(null);
    const {
        refs,
        floatingStyles,
        context,
        middlewareData,
        placement: resolvedPlacement,
    } = useFloating({
        placement,
        open,
        onOpenChange: handleOpenChange,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(offsetValue),
            flip({ padding: 8 }),
            shift({ padding: 8 }),
            floatingArrow({ element: arrowRef }),
        ],
    });

    const hover = useHover(context, {
        enabled: triggers.has("hover"),
        move: false,
        handleClose: interactive && locked ? safePolygon() : undefined,
    });
    const focus = useFocus(context, { enabled: triggers.has("focus") });
    const click = useClick(context, { enabled: triggers.has("click") });
    const dismiss = useDismiss(context, { enabled: !triggers.has("manual") });
    const role = useRole(context, { role: "tooltip" });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss, role]);

    const arrowX = middlewareData.arrow?.x;
    const arrowY = middlewareData.arrow?.y;
    const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
    }[resolvedPlacement.split("-")[0]] as keyof CSSProperties;

    const arrowStyle: CSSProperties = {
        left: arrowX != null ? `${arrowX}px` : "",
        top: arrowY != null ? `${arrowY}px` : "",
        [staticSide]: "-4px",
    };

    useEffect(() => {
        if (!interactive || !open) {
            setLocked(false);
            return;
        }
        setLocked(false);
        const timer = window.setTimeout(() => setLocked(true), TOOLTIP_LOCK_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [interactive, open]);

    const tooltipStyle = {
        ...floatingStyles,
        ...style,
        ["--tooltip-lock-delay" as string]: `${TOOLTIP_LOCK_DELAY_MS}ms`,
    } as CSSProperties;

    const mergeRefs =
        <T,>(...refs: Array<Ref<T> | undefined>) =>
        (node: T) => {
            refs.forEach((ref) => {
                if (!ref) return;
                if (typeof ref === "function") {
                    ref(node);
                } else {
                    (ref as { current: T | null }).current = node;
                }
            });
        };

    const childRef = (children as ReactElement & { ref?: Ref<unknown> }).ref;

    return (
        <>
            {cloneElement(children, {
                ref: mergeRefs(childRef, refs.setReference),
                ...getReferenceProps(children.props),
            })}
            {open ? (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className={`userful-tooltip ${
                            interactive
                                ? locked
                                    ? "userful-tooltip--interactive userful-tooltip--locked"
                                    : "userful-tooltip--interactive userful-tooltip--locking"
                                : "userful-tooltip--static"
                        } ${className || ""}`.trim()}
                        style={tooltipStyle}
                        {...getFloatingProps({
                            onMouseEnter,
                            onMouseLeave,
                        })}
                    >
                        {title}
                        <div
                            ref={arrowRef}
                            className={`userful-tooltip-arrow ${arrowClassName || ""}`.trim()}
                            style={arrowStyle}
                        />
                    </div>
                </FloatingPortal>
            ) : null}
        </>
    );
}
